import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Alert, Upload, Form, Row, Col, Spin } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { UploadChangeParam } from 'antd/lib/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import ApiService from '@services/apiService';
import { message } from 'antd';
import { useUser } from '@hooks/users/useUser';
import { useQueryClient } from '@tanstack/react-query';

interface EditUserModalProps {
  visible: boolean;
  onClose: () => void;
  user: User | null;
  onSuccess: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ visible, onClose, user, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [idImage1, setIdImage1] = useState<any>(null);
  const [idImage2, setIdImage2] = useState<any>(null);
  const queryClient = useQueryClient();

  const { data: fullUserData, isLoading: loadingUserData } = useUser(
    visible && user?.id ? user.id : undefined
  );

  useEffect(() => {
    if (fullUserData) {
      form.setFieldsValue({
        firstName: fullUserData.firstName,
        lastName: fullUserData.lastName,
        email: fullUserData.email,
        address: fullUserData.address || '',
        address2: fullUserData.address2 || '',
        city: fullUserData.city || '',
        state: fullUserData.state || '',
        zip: fullUserData.zip || '',
        country: fullUserData.country || '',
      });

      // Set existing ID images if available
      if (fullUserData.onboarding) {
        setIdImage1(fullUserData.onboarding.idImage1);
        setIdImage2(fullUserData.onboarding.idImage2);
      }
    } else if (user && visible && !loadingUserData) {
      // Fallback to basic user data
      form.setFieldsValue({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        address: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        country: '',
      });
    }
  }, [fullUserData, user, visible, loadingUserData, form]);

  const getUploadProps = (imageIndex: number) => {
    const userId = fullUserData?.id || user?.id;
    return {
      name: 'file',
      action: ApiService.getUrl(`/users/current-user/images/${imageIndex}/${userId}`),
      headers: ApiService.getUploadHeaders(),
      accept: 'image/*',
      beforeUpload: (file: any) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
          message.error('You can only upload image files!');
          return false;
        }
        const isLt10M = file.size / 1024 / 1024 < 10;
        if (!isLt10M) {
          message.error('Image must be smaller than 10MB!');
          return false;
        }
        return true;
      },
      onChange(info: UploadChangeParam<UploadFile>) {
        console.log('Upload info:', info);
        if (info.file.status === 'uploading') {
          console.log(`Uploading ID image ${imageIndex}...`);
        } else if (info.file.status === 'done') {
          console.log('Upload response:', info.file.response);
          message.success(`${info.file.name} file uploaded successfully`);
          if (imageIndex === 1) {
            setIdImage1(info.file.response);
          } else {
            setIdImage2(info.file.response);
          }
        } else if (info.file.status === 'error') {
          console.error('Upload error:', info.file.error);
          message.error(
            `${info.file.name} file upload failed: ${info.file.error?.message || 'Unknown error'}`
          );
        }
      },
    };
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      const values = await form.validateFields();
      setLoading(true);
      setError(undefined);

      await ApiService.put(`/users/${user.id}`, values);

      queryClient.invalidateQueries({ queryKey: ['user', user.id] });

      message.success('User updated successfully');
      setLoading(false);
      onSuccess();
      onClose();
    } catch (err: any) {
      setLoading(false);
      if (err && err.response && err.response.data) {
        setError(`Error occurred. ${err.response.data.message || 'Unknown error'}`);
      } else {
        setError('Error occurred while updating user.');
      }
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setError(undefined);
    setIdImage1(null);
    setIdImage2(null);
    onClose();
  };

  return (
    <Modal
      title={`Edit User: ${user?.firstName} ${user?.lastName}`}
      visible={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={loading} onClick={handleSave}>
          Save Changes
        </Button>,
      ]}
      width={800}
      destroyOnClose
    >
      {error && <Alert style={{ marginBottom: 16 }} type="error" message={error} />}

      {loadingUserData ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Loading user data...</p>
        </div>
      ) : (
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'Please enter first name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: 'Please enter last name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="address" label="Address">
            <Input />
          </Form.Item>

          <Form.Item name="address2" label="Address 2">
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="city" label="City">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="state" label="State">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="zip" label="ZIP Code">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="country" label="Country">
            <Input />
          </Form.Item>

          <div
            style={{
              marginTop: 24,
              padding: '16px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              backgroundColor: '#fafafa',
            }}
          >
            <h4 style={{ marginBottom: 16 }}>ID Images</h4>
            <p style={{ marginBottom: 16, color: '#666' }}>
              Upload government-issued identification documents (passport, driver's license, etc.)
            </p>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: 16 }}>
                  <strong style={{ marginRight: 8 }}>ID Image 1 (Front):</strong>
                  {idImage1 && (
                    <div
                      style={{
                        marginTop: 8,
                        marginBottom: 8,
                        padding: '8px',
                        backgroundColor: '#e6f7ff',
                        borderRadius: '4px',
                      }}
                    >
                      <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                        ✓ Current ID uploaded
                      </span>
                      <br />
                      <a
                        href={idImage1.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: '12px' }}
                      >
                        View current ID #1
                      </a>
                    </div>
                  )}
                  <Upload {...getUploadProps(1)} showUploadList={false}>
                    <Button icon={<UploadOutlined />} block>
                      {idImage1 ? 'Replace ID #1' : 'Upload ID #1'}
                    </Button>
                  </Upload>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: 16 }}>
                  <strong style={{ marginRight: 8 }}>ID Image 2 (Back):</strong>
                  {idImage2 && (
                    <div
                      style={{
                        marginTop: 8,
                        marginBottom: 8,
                        padding: '8px',
                        backgroundColor: '#e6f7ff',
                        borderRadius: '4px',
                      }}
                    >
                      <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                        ✓ Current ID uploaded
                      </span>
                      <br />
                      <a
                        href={idImage2.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: '12px' }}
                      >
                        View current ID #2
                      </a>
                    </div>
                  )}
                  <Upload {...getUploadProps(2)} showUploadList={false}>
                    <Button icon={<UploadOutlined />} block>
                      {idImage2 ? 'Replace ID #2' : 'Upload ID #2'}
                    </Button>
                  </Upload>
                </div>
              </Col>
            </Row>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default EditUserModal;
